import {
  and,
  collection,
  documentId,
  getCountFromServer,
  getDocs,
  or,
  orderBy,
  Query,
  query,
  where,
} from 'firebase/firestore';
import Failure from '../../../core/Failure';
import { FirestoreCollections, firestore } from '../../../firebase.config';
import { IPlan } from '@leedpulse-monorepo/interfaces';
import { EProductType } from '@leedpulse-monorepo/enums';

const plansCollection = collection(firestore, FirestoreCollections.plans);
const subscriptionsCollection = collection(
  firestore,
  FirestoreCollections.subscriptions
);

/**
 * Builds the search filter constraint for documentId or name matching
 */
function buildSearchFilter(searchTerm: string) {
  return or(
    where(documentId(), '==', searchTerm),
    where('name', '==', searchTerm)
  );
}

/**
 * Builds a query for plans with optional search term
 */
function buildPlansQuery(product: EProductType, searchTerm?: string): Query {
  const productFilter = where('product', '==', product);

  const constraints = searchTerm
    ? and(productFilter, buildSearchFilter(searchTerm))
    : and(productFilter);

  return query(plansCollection, constraints);
}

/**
 * Gets the count of plans for a specific product type
 */
async function getProductCount(
  product?: EProductType,
  searchTerm?: string
): Promise<number> {
  const snapshot = product
    ? await getCountFromServer(buildPlansQuery(product, searchTerm))
    : await getCountFromServer(plansCollection);
  return snapshot.data().count ?? 0;
}

/**
 * Gets the count of subscriptions for a specific plan
 */
async function getSubscriptionsCountByPlanId(planId: string): Promise<number> {
  const subscriptionQuery = query(
    subscriptionsCollection,
    where('id_plan', '==', planId)
  );
  const snapshot = await getCountFromServer(subscriptionQuery);
  return snapshot.data().count ?? 0;
}

/**
 * Gets the subscription counts for multiple plans in parallel
 */
async function getSubscriptionsCountsForPlans(
  planIds: string[]
): Promise<Record<string, number>> {
  const counts = await Promise.all(
    planIds.map(async (planId) => ({
      planId,
      count: await getSubscriptionsCountByPlanId(planId),
    }))
  );

  return counts.reduce((acc, { planId, count }) => {
    acc[planId] = count;
    return acc;
  }, {} as Record<string, number>);
}

async function searchPlans(
  searchQuery: string,
  searchProduct: EProductType
): Promise<{
  plans: IPlan[];
  totalCollectionCount: number;
  productCounts: {
    [EProductType.LEEDPULSE]: number;
    [EProductType.LEEDFLOW]: number;
  };
}> {
  try {
    const sanitizedSearchQuery = searchQuery.trim() || undefined;

    // Fetch plans matching the search criteria with ordering
    const plansQuery = query(
      buildPlansQuery(searchProduct, sanitizedSearchQuery),
      orderBy('name', 'asc')
    );
    const searchResults = await getDocs(plansQuery);
    let plans = searchResults.docs.map((doc) => doc.data() as IPlan);

    // Extract plan IDs for subscription count queries
    const planIds = plans.map((plan) => plan.id);

    // Fetch counts in parallel for better performance
    const [
      totalCollectionCount,
      leedpulseCount,
      leedflowCount,
      subscriptionsCounts,
    ] = await Promise.all([
      getProductCount(),
      getProductCount(EProductType.LEEDPULSE, sanitizedSearchQuery),
      getProductCount(EProductType.LEEDFLOW, sanitizedSearchQuery),
      getSubscriptionsCountsForPlans(planIds),
    ]);

    if (subscriptionsCounts) {
      plans = plans.map((plan) =>
        ({
          ...plan,
          subscriptionsCount: subscriptionsCounts[plan.id] ?? 0,
        })
      );
    }

    return {
      plans,
      totalCollectionCount,
      productCounts: {
        [EProductType.LEEDPULSE]: leedpulseCount,
        [EProductType.LEEDFLOW]: leedflowCount,
      },
    };
  } catch (e) {
    console.log('error: ', e);
    throw new SearchPlansFailure();
  }
}

export class SearchPlansFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default searchPlans;
